import { createAsyncThunk } from '@reduxjs/toolkit'
import {
  fetchClasses,
  fetchPolicies,
  fetchComparisonOptions,
  fetchFeatures,
  fetchPolicyContentsById,
  fetchSectionContentsById,
  saveComparisons,
  getSavedComparisons,
  removeSavedComparison,
  expungeSavedComparison,
  updateSavedComparison,
  emailSavedComparison,
  getSavedComparison,
  fetchBuyerGroups,
  approvePolicy,
  fetchInsurers,
} from './api'
import { IRootState } from 'store'

export const fetchPolicyContentByIdAsync = createAsyncThunk(
  'comparison/fetch-policy-findings',
  fetchPolicyContentsById,
)
export const fetchSectionContentByIdAsync = createAsyncThunk(
  'comparison/fetch-feature-findings',
  fetchSectionContentsById,
)
export const fetchClassesAsync = createAsyncThunk('comparison/fetch-classes', fetchClasses)

/**
 * Loads the real class catalogue if all we have is the bundled seed. Anything that has to
 * decide what a cover id means needs this first: covers added since the seed was written
 * are absent from it, and it holds AU classes only, so deciding against it both discards
 * covers that do exist and leaves ids no cover picker can name.
 *
 * A failed fetch leaves the flag down so the next caller tries again, and leaves the seed
 * in place, which is what callers had before.
 */
export const ensureProductClassesAsync = createAsyncThunk(
  'comparison/ensure-product-classes',
  async (_arg: void, { getState, dispatch }) => {
    const state = getState() as IRootState
    if (state.comparison.productClassesLoaded) return
    // Same precedence as SelectCovers, so the two cannot fetch different countries.
    const country =
      state.comparison.impersonation?.selectedCountry ||
      state.auth?.account?.buy_group?.country_code ||
      'au'
    await dispatch(fetchClassesAsync(country.toLowerCase()))
  },
)
export const fetchPoliciesAsync = createAsyncThunk('comparison/fetch-policies', fetchPolicies)
export const fetchFeaturesAsync = createAsyncThunk('comparison/fetch-features', fetchFeatures)
export const fetchComparisonOptionsAsync = createAsyncThunk(
  'comparison/fetch-comparison-options',
  fetchComparisonOptions,
)
export const saveComparisonsAsync = createAsyncThunk('comparison/save-comparison', saveComparisons)
export const getSavedComparisonsAsync = createAsyncThunk(
  'comparison/fetch-saved-comparisons',
  getSavedComparisons,
)
export const removeSavedComparisonAsync = createAsyncThunk(
  'comparison/remove-saved-comparison',
  removeSavedComparison,
)
/**
 * Permanently clears an already-deleted comparison. DELETE only soft-deletes, and the
 * hidden row keeps its name — so saving a new comparison under that name collides with
 * it and the API answers "no change found". Only ever call this on a deleted comparison.
 */
export const expungeSavedComparisonAsync = createAsyncThunk(
  'comparison/expunge-saved-comparison',
  expungeSavedComparison,
)
export const emailSavedComparisonAsync = createAsyncThunk(
  'comparison/email-saved-comparison',
  emailSavedComparison,
)
export const getSavedComparisonAsync = createAsyncThunk(
  'comparison/get-saved-comparison',
  getSavedComparison,
)

export const updateSavedComparisonAsync = createAsyncThunk(
  'comparison/update-saved-comparison',
  updateSavedComparison,
)

export const fetchBuyerGroupsAsync = createAsyncThunk(
  'comparison/fetch-buyer-groups',
  fetchBuyerGroups,
)

export const fetchInsurersAsync = createAsyncThunk(
  'comparison/fetch-insurers-for-tmd',
  fetchInsurers,
)

export const approvePolicyAsync = createAsyncThunk('comparison/approve-policy', approvePolicy)
